import type { FC } from "react";

import { useSearchParams } from "react-router-dom";

import type { TaskSnapshot } from "../../types";

import { useTaskDetails } from "../../hooks/useTaskDetails";
import { useTaskLogs } from "../../hooks/useTaskLogs";
import { updateSearchParams } from "../../utils/searchParams";
import { TaskDetailsSheet } from "./TaskDetailsSheet";

const PROGRESS_EXPANDED_PARAM = "task_progress_expanded";

export const TaskDetailsDrawer: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const taskId = searchParams.get("taskId");
  const progressExpanded = searchParams.get(PROGRESS_EXPANDED_PARAM) === "1";
  const isOpen = Boolean(taskId);

  const details = useTaskDetails(taskId ?? "", isOpen);
  const logs = useTaskLogs(taskId ?? "", isOpen);

  const handleClose = () => {
    setSearchParams((prev) =>
      updateSearchParams(prev, {
        taskId: null,
        [PROGRESS_EXPANDED_PARAM]: null,
      }),
    );
  };

  const handleProgressExpandedChange = (expanded: boolean) => {
    setSearchParams(
      (prev) =>
        updateSearchParams(prev, {
          [PROGRESS_EXPANDED_PARAM]: expanded ? "1" : null,
        }),
      { replace: true },
    );
  };

  if (!isOpen || !taskId) {
    return null;
  }

  // We need at least a snapshot to render the sheet.
  // Ideally, useTaskDetails returns the snapshot as 'data'.
  // If 'data' is null but 'loading' is true, we can still render the sheet
  // But let the sheet handle the empty/loading state internally if we pass a partial/null snapshot?
  // However, `TaskDetailsSheet` expects `TaskSnapshot`.
  //
  // Let's create a minimal placeholder snapshot if data is not yet available but we have an ID.
  // This allows the sheet to open immediately while loading.

  const now = Date.now();
  const placeholderSnapshot: TaskSnapshot = {
    assigned_node: null,
    attempt_count: 0,
    completed_at: null,
    cost_usd: null,
    created_at: now,
    depends_on_task_id: null,
    error: null,
    flow_run_id: null,
    has_details: false,
    last_callback: null,
    linked_task_id: null,
    linked_ticket_id: null,
    metadata: {},
    payload: {},
    result: null,
    started_at: null,
    status: "queued",
    task_id: taskId,
    type: "unknown",
    updated_at: now,
  };

  const snapshot = details.data ?? placeholderSnapshot;

  return (
    <TaskDetailsSheet
      isOpen={isOpen}
      onClose={handleClose}
      snapshot={snapshot}
      details={details}
      logs={logs}
      providerProgressExpanded={progressExpanded}
      onProviderProgressExpandedChange={handleProgressExpandedChange}
    />
  );
};
